/**
 * Reusable Delete Confirmation Dialog
 * Provides consistent delete confirmation UX across the application
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface DeleteConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  itemName?: string;
  message?: string;
  loading?: boolean;
}

export const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  itemName,
  message,
  loading = false,
}) => {
  const { t } = useTranslation();

  const defaultTitle = t('common.confirmDelete', 'Confirm Delete');
  const defaultMessage = itemName
    ? t('common.confirmDeleteItem', 'Are you sure you want to delete "{{name}}"? This action cannot be undone.', { name: itemName })
    : t('common.confirmDeleteGeneric', 'Are you sure you want to delete this item? This action cannot be undone.');

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {title || defaultTitle}
      </DialogTitle>
      
      <DialogContent>
        <Typography>
          {message || defaultMessage}
        </Typography>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={onClose}
          disabled={loading}
        >
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button 
          onClick={onConfirm}
          variant="contained"
          color="error"
          disabled={loading}
        >
          {loading ? t('common.deleting', 'Deleting...') : t('common.delete', 'Delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};